import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import routes from 'router/routes';

import { trackScreenLoad } from 'services/Analytics/trackers/mainTrackers';
import { sendAnalyticMarkAsCompleteClick, sendPlayerTrainingComplete } from 'services/Analytics/trackers/workoutPlayer';

import { useGetPPCommonAnalyticsData } from 'hooks/workout';

import { CustomPrimaryButton } from 'components/Buttons';
import CustomSurfaceMainButton from 'components/Buttons/CustomSurfaceMainButton';
import Modal from 'components/Modal/Modal';

import { QuitModalPropsType } from './types';

import * as S from './styled';

const QuitWorkoutModal = ({
    closeModal,
    handlePlayButton,
    handleCompleteWorkout,
    totalProgress,
    currentExercise,
}: QuitModalPropsType) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isMarkAsCompleted, setMarkAsCompleted] = useState<boolean>(false);

    const commonAnalyticDate = useGetPPCommonAnalyticsData();

    useEffect(() => {
        trackScreenLoad('player_interrupt', {
            ...commonAnalyticDate,
            workout_part: currentExercise.block_type,
            fact_duration: totalProgress,
        });
    }, []);

    const handleQuit = () => {
        sendPlayerTrainingComplete({
            ...commonAnalyticDate,
            completion_type: 'close',
            fact_duration: totalProgress,
        });

        closeModal();
        navigate(routes.PRIVATE.PERSONAL_PLAN.path);
    };

    const handleComplete = () => {
        sendPlayerTrainingComplete({
            ...commonAnalyticDate,
            completion_type: 'finish',
            fact_duration: totalProgress,
        });
        closeModal();
        handleCompleteWorkout();
    };

    const handleResume = () => {
        closeModal();
        handlePlayButton();
    };

    const handleMarkAsCompleted = () => {
        sendAnalyticMarkAsCompleteClick({
            ...commonAnalyticDate,
            exercise_name: currentExercise.name,
            exercise_id: currentExercise.exercise_id,
            complete: !isMarkAsCompleted,
            fact_duration: totalProgress,
            workout_part: currentExercise.block_type,
        });

        setMarkAsCompleted(!isMarkAsCompleted);
    };

    return (
        <Modal isOpen shouldCloseOnOverlayClick paddingX={20} paddingY={0} customContent>
            <S.ModalWrapper>
                <S.Content>
                    <S.Title medium type="h4" mb={12}>
                        {t('workout.quitWorkoutModal.title')}
                    </S.Title>
                    <S.Subtitle type="large-text" mb={24}>
                        {t('workout.quitWorkoutModal.subtitle')}
                    </S.Subtitle>
                    <S.Checkbox
                        dataLocator="completeButton"
                        titleType="medium-button"
                        title={t('workout.quitWorkoutModal.markAsCompleted')}
                        value=""
                        leftCheckbox
                        onClick={handleMarkAsCompleted}
                    />
                    <S.ButtonWrapper>
                        {isMarkAsCompleted ? (
                            <CustomPrimaryButton dataLocator="successfullyCompleteDayButton" onClick={handleComplete}>
                                {t('workout.quitWorkoutModal.completeBtn')}
                            </CustomPrimaryButton>
                        ) : (
                            <>
                                <CustomSurfaceMainButton dataLocator="quitButton" onClick={handleQuit}>
                                    {t('workout.quitWorkoutModal.quitBtn')}
                                </CustomSurfaceMainButton>
                                <CustomPrimaryButton dataLocator="resumeButton" onClick={handleResume}>
                                    {t('workout.quitWorkoutModal.resumeBtn')}
                                </CustomPrimaryButton>
                            </>
                        )}
                    </S.ButtonWrapper>
                </S.Content>
            </S.ModalWrapper>
        </Modal>
    );
};

export default QuitWorkoutModal;
